import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.api';
import { invitationsApi } from '../../api/invitations.api';
import { getAccessToken } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import Spinner from '../../components/ui/Spinner';
import { AtSign, ArrowRight, Icon } from '../../components/ui/Icon';
import { CountrySelect } from '../../components/ui/CountrySelect';
import { guessCountryFromLocale } from '../../utils/countries';

export default function CompleteProfilePage() {
  const { user, setAuth } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const redirectTo = (redirectParam && redirectParam.startsWith('/')) ? redirectParam : '/dashboard';

  const [username, setUsername] = useState(user?.username ?? '');
  const [nationalityCode, setNationalityCode] = useState(user?.nationalityCode ?? guessCountryFromLocale());
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.completeProfile({ username: username.trim(), nationalityCode, acceptedTerms: true });
      const token = getAccessToken();
      const me = await authApi.me();
      if (token) setAuth(me, token);

      // If the redirect is an invitation accept URL, consume it here directly
      // to avoid a race with AcceptInvitePage's auto-accept useEffect.
      if (redirectTo.startsWith('/invite/accept')) {
        const inviteToken = new URLSearchParams(redirectTo.split('?')[1] ?? '').get('token');
        if (inviteToken) {
          try {
            const { tripId } = await invitationsApi.accept(inviteToken);
            navigate(`/trips/${tripId}/itinerary`, { replace: true });
            return;
          } catch { /* fall through to redirect — the AcceptInvitePage will show the error */ }
        }
      }

      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('auth.completeProfile.saveFailed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
      <div className="w-full max-w-md px-8">
        <p className="font-mono text-[10px] tracking-[2px] uppercase mb-3" style={{ color: '#8a5a2b' }}>
          {t('auth.completeProfile.eyebrow')}
        </p>
        <h1 className="font-serif text-[42px] font-light leading-none tracking-tight mb-4" style={{ color: '#0b1420', letterSpacing: -1.6 }}>
          {t('auth.completeProfile.title')}
        </h1>
        <p className="font-serif font-medium text-base leading-relaxed mb-8" style={{ color: '#3a4558' }}>
          {t('auth.completeProfile.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
              {t('auth.completeProfile.nicknameLabel')}
            </label>
            <div className="flex items-center gap-2.5" style={{ border: '1px solid #d6dae2', borderRadius: 10, height: 54, background: '#fff', padding: '0 14px' }}>
              <Icon as={AtSign} size={16} style={{ color: '#6b7689', flexShrink: 0 }} />
              <input
                type="text"
                className="flex-1 outline-none bg-transparent text-sm"
                style={{ fontFamily: 'Inter, sans-serif', color: '#0b1420', height: '100%', border: 'none' }}
                placeholder="your_nickname"
                value={username}
                onChange={e => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30))}
                required
                minLength={3}
                maxLength={30}
                pattern="[a-zA-Z0-9_]+"
                autoFocus
              />
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: '#6b7689' }}>
              {t('auth.completeProfile.nicknameHint')}
            </p>
          </div>

          {/* Nationality — drives personalised entry requirements */}
          <div>
            <label className="block font-mono text-[10px] tracking-[1.6px] uppercase mb-2" style={{ color: '#6b7689' }}>
              {t('auth.register.nationality')}
            </label>
            <CountrySelect value={nationalityCode} onChange={setNationalityCode} />
          </div>

          {/* Terms */}
          <div className="flex items-start gap-2.5 text-sm" style={{ color: '#3a4558' }}>
            <button type="button" onClick={() => setAcceptedTerms(v => !v)}
              className="flex items-center justify-center mt-0.5"
              style={{ width: 18, height: 18, border: `1.4px solid ${acceptedTerms ? '#0b1420' : '#d6dae2'}`, borderRadius: 4, background: acceptedTerms ? '#0b1420' : '#fff', flexShrink: 0, cursor: 'pointer' }}
            >
              {acceptedTerms && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#faf7f0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="5 12 10 17 20 7" />
                </svg>
              )}
            </button>
            <span>
              {t('auth.completeProfile.acceptStart')}{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
                {t('auth.completeProfile.terms')}
              </Link>
              {' '}{t('auth.completeProfile.and')}{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#0b1420', borderBottom: '1px solid #0b1420', paddingBottom: 1 }}>
                {t('auth.completeProfile.privacy')}
              </Link>
              .
            </span>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">⚠ {error}</p>}

          <button
            type="submit"
            disabled={loading || !acceptedTerms || username.trim().length < 3}
            className="w-full flex items-center justify-center gap-3 font-serif font-medium text-lg transition-all"
            style={{ marginTop: 12, height: 56, background: '#0b1420', color: '#faf7f0', borderRadius: 10, border: 'none', cursor: loading ? 'wait' : 'pointer', opacity: !acceptedTerms || username.trim().length < 3 ? 0.5 : 1 }}
          >
            {loading ? <Spinner size="sm" /> : (
              <>
                {t('auth.completeProfile.continue')}
                <span className="w-7 h-7 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
                  <Icon as={ArrowRight} size={13} />
                </span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
